// Generic Carousel Modal used by every airline's Creativity slides.
// No airline-specific strings — data comes from the airline content payload.
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export const CarouselModal = ({ items, title, accentColor, startIdx = 0, onClose }) => {
  const { isDark } = useTheme();
  const [idx, setIdx] = useState(startIdx);

  const prev = () => setIdx((i) => (i === 0 ? items.length - 1 : i - 1));
  const next = () => setIdx((i) => (i === items.length - 1 ? 0 : i + 1));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const item = items[idx] || {};
  const hasImage = !!item.image;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
      <div className={`absolute inset-0 ${isDark ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm`} />
      <div
        className={`relative w-full max-w-5xl mx-4 rounded-2xl overflow-hidden ${
          isDark ? "bg-[#0b0b0d] border border-white/10" : "bg-white border border-black/10 shadow-2xl"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-3 border-b ${isDark ? "border-white/10" : "border-black/10"}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}20`, border: `1.5px solid ${accentColor}40` }}>
              <span className="text-xs font-bold" style={{ color: accentColor }}>{idx + 1}</span>
            </div>
            <div>
              <div className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{title}</div>
              <div className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{idx + 1} of {items.length}</div>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-black/[0.05] text-slate-500"}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {item.customType === "yearStrategy" ? (
          <div className="w-full">
            <YearStrategySlide {...item.customData} isDark={isDark} />
          </div>
        ) : item.videoEmbed ? (
          <div className="w-full bg-black" style={{ aspectRatio: "16/9" }}>
            <iframe
              key={`${idx}-${item.videoEmbed}`}
              src={item.videoEmbed}
              title={item.title}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              data-testid="carousel-video-iframe"
            />
          </div>
        ) : item.tags ? (
          <div className="flex gap-0" style={{ minHeight: "320px" }}>
            <div className="flex-1 px-6 py-6 flex flex-col justify-center">
              <h3 className={`text-xl font-bold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>{item.title}</h3>
              <p className={`text-base leading-relaxed mb-4 ${isDark ? "text-slate-200" : "text-slate-700"}`}>{item.desc}</p>
              <div className="flex flex-wrap gap-1.5">
                {item.tags.map((tag, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>{tag}</span>
                ))}
              </div>
            </div>
            {item.image && (
              <div className="w-[45%] flex-shrink-0 overflow-hidden" style={{ aspectRatio: "10/11" }}>
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: item.imagePosition || "center" }}
                />
              </div>
            )}
          </div>
        ) : hasImage ? (
          <div className="w-full">
            <img src={item.image} alt={item.title} className="w-full h-auto object-contain" />
          </div>
        ) : (
          <div className="px-8 py-12 flex flex-col items-center text-center" style={{ minHeight: "40vh" }}>
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${accentColor}15`, border: `2px dashed ${accentColor}40` }}>
              <span className="text-2xl font-bold" style={{ color: accentColor }}>{idx + 1}</span>
            </div>
            <h3 className={`text-2xl font-bold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>{item.title}</h3>
            <p className={`text-base leading-relaxed max-w-xl ${isDark ? "text-slate-300" : "text-slate-600"}`}>{item.desc}</p>
          </div>
        )}

        {/* Navigation */}
        <div className={`flex items-center justify-between px-6 py-3 border-t ${isDark ? "border-white/10" : "border-black/10"}`}>
          <button onClick={prev} className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${isDark ? "bg-white/[0.06] text-white hover:bg-white/10" : "bg-black/[0.04] text-slate-900 hover:bg-slate-200"}`}>
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <div className="flex gap-1.5">
            {items.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className="w-2 h-2 rounded-full transition-all" style={{ backgroundColor: i === idx ? accentColor : isDark ? "#475569" : "#cbd5e1", transform: i === idx ? "scale(1.3)" : "scale(1)" }} />
            ))}
          </div>
          <button onClick={next} className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${isDark ? "bg-white/[0.06] text-white hover:bg-white/10" : "bg-black/[0.04] text-slate-900 hover:bg-slate-200"}`}>
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Generic Year Strategy renderer (data-driven, no airline strings) ──
import { resolveIcon } from "./iconMap";
import { Sparkles } from "lucide-react";

const yearAccents = {
  1: { color: "#10B981", iconName: "Leaf" },
  2: { color: "#3B82F6", iconName: "Trophy" },
  3: { color: "#A855F7", iconName: "Rocket" },
};

export const YearStrategySlide = ({ year, mainTheme, clusters, isDark }) => {
  const meta = yearAccents[year] || yearAccents[1];
  const HeaderIcon = resolveIcon(meta.iconName);
  return (
    <div className="px-6 py-5">
      <div className="flex items-center gap-4 mb-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${meta.color}1F`, border: `2px solid ${meta.color}55` }}
        >
          <HeaderIcon className="w-6 h-6" style={{ color: meta.color }} />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: meta.color }}>
            Year {year} · Main Theme
          </div>
          <div className={`text-2xl font-bold leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
            {mainTheme}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {clusters.map((c, i) => {
          const Icon = c.iconName ? resolveIcon(c.iconName) : Sparkles;
          return (
            <div key={i} className={`rounded-xl p-4 border ${isDark ? "bg-white/[0.03] border-white/10" : "bg-black/[0.02] border-black/10"}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}1F` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{c.label}</div>
              </div>
              <ul className="space-y-2">
                {c.bullets.map((b, j) => (
                  <li key={j} className={`flex items-start gap-2 text-sm leading-snug ${isDark ? "text-slate-200" : "text-slate-600"}`}>
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              {c.pills && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {c.pills.map((p, j) => (
                    <span key={j} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${meta.color}1F`, color: meta.color }}>{p}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
